// Resolves an outbound API call against the demo fixture set.
//
// Every request the app makes in demo mode is answered here; none reach the
// network. A path no fixture claims still answers successfully with an empty
// collection, so an unseeded corner of the product renders as "nothing yet"
// rather than as a broken page.

import type { PbApiOptions } from '~/composables/api';
import type { DemoRequest, DemoRoute } from './types';
import { billingRoutes } from './routes/billing';
import { contentRoutes } from './routes/content';
import { discoveryRoutes } from './routes/discovery';
import { exposureRoutes } from './routes/exposures';
import { footprintRoutes } from './routes/footprint';
import { householdRoutes } from './routes/household';
import { identityRoutes } from './routes/identity';
import { licenseGiftRoutes } from './routes/license-gifts';

const routes: DemoRoute[] = [
    ...identityRoutes,
    ...exposureRoutes,
    ...footprintRoutes,
    ...discoveryRoutes,
    ...contentRoutes,
    ...billingRoutes,
    ...licenseGiftRoutes,
    ...householdRoutes,
];

/** Simulated network latency, so loading states are exercised instead of skipped. */
const DEMO_LATENCY_MS = 180;

function methodsOf(route: DemoRoute): string[] {
    const method = route.method ?? 'GET';
    return (Array.isArray(method) ? method : [method]).map((m) => m.toUpperCase());
}

function parseBody(body: any): any {
    if (typeof body !== 'string') {
        return body;
    }

    try {
        return JSON.parse(body);
    } catch {
        return body;
    }
}

// The live answer's shape: a fixture is never throttled, so it names no wait.
export type DemoResult<T> = { data: T; status: number; message: string; retryAfter: null };

export async function resolveDemoRequest<T>(url: string, options: PbApiOptions = {}): Promise<DemoResult<T>> {
    const [rawPath, rawQuery = ''] = String(url).split('?');
    const path = rawPath.replace(/^\/+/, '');
    const method = String(options?.method ?? 'GET').toUpperCase();

    await new Promise((resolve) => setTimeout(resolve, DEMO_LATENCY_MS));

    for (const route of routes) {
        if (!methodsOf(route).includes(method)) {
            continue;
        }

        const match = path.match(route.pattern);
        if (!match) {
            continue;
        }

        const request: DemoRequest = {
            path,
            query: new URLSearchParams(rawQuery),
            method,
            body: parseBody(options?.body),
            match,
        };

        return {
            data: route.respond(request) as T,
            status: typeof route.status === 'function' ? route.status(request) : route.status ?? 200,
            message: '',
            retryAfter: null,
        };
    }

    // Reads fall back to an empty collection; writes report success so demo
    // interactions (save, dismiss, delete) complete instead of erroring.
    console.warn(`[demo] no fixture for ${method} /${path} — answering empty`);

    return {
        data: (method === 'GET' ? { data: [] } : { data: null }) as T,
        status: 200,
        message: '',
        retryAfter: null,
    };
}
